import {
  FormControl,
  FormHelperText,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import { Controller, useFormContext } from "react-hook-form";

import type {
  AdditionalFormFieldProps,
  BaseFormFieldDefinition,
} from "../form-field";

export const DATE_FORMAT = "YYYY-MM-DD";
export const DATE_FORM_FIELD_TYPE = "date" as const;

export type DateFormFieldDefinition = BaseFormFieldDefinition<
  typeof DATE_FORM_FIELD_TYPE
> & {
  type: typeof DATE_FORM_FIELD_TYPE;
  disabled?: boolean;
  format?: string;
  value: string;
  popperPlacement?: "bottom-start" | "bottom-end";
  enableAutoFlip?: boolean;
};

const FALLBACK_PLACEMENTS: Record<"bottom-start" | "bottom-end", string[]> = {
  "bottom-start": ["top-start"],
  "bottom-end": ["top-end"],
};

export function DateFormField({
  errorMessage,
  name,
  disabled,
  format = DATE_FORMAT,
  sx,
  popperPlacement = "bottom-end",
  enableAutoFlip = false,
}: DateFormFieldDefinition & AdditionalFormFieldProps) {
  const { control } = useFormContext();
  const modifiers = enableAutoFlip
    ? [
        {
          name: "flip",
          enabled: true,
          options: {
            fallbackPlacements: FALLBACK_PLACEMENTS[popperPlacement],
          },
        },
        {
          name: "preventOverflow",
          enabled: true,
          options: {
            boundary: "viewport",
            padding: 8,
          },
        },
      ]
    : undefined;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, ...field } }) => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <FormControl error={!!errorMessage} fullWidth>
            <DatePicker
              disabled={disabled}
              PopperProps={{
                disablePortal: !enableAutoFlip,
                modifiers,
                placement: popperPlacement,
                sx: {
                  zIndex: 1301,
                  "& .MuiPaper-root": {
                    marginTop: "8px",
                  },
                },
              }}
              renderInput={(params: TextFieldProps) => (
                <TextField
                  {...params}
                  error={!!errorMessage}
                  sx={sx}
                  InputProps={{
                    ...params.InputProps,
                    style: {
                      borderRadius: "6px",
                      height: "45px",
                    },
                  }}
                />
              )}
              inputFormat={format}
              {...field}
              onChange={(date: Dayjs | null) => {
                onChange(date?.format(format) ?? "");
              }}
            />
            {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
          </FormControl>
        </LocalizationProvider>
      )}
    ></Controller>
  );
}
